import React, { useState, useEffect, ReactNode, ErrorInfo } from 'react';
import { Header } from './components/Header';
import { OnboardingStep } from './components/OnboardingStep';
import { ScenarioStep } from './components/ScenarioStep';
import { ChatInterface } from './components/ChatInterface';
import { HomeStep } from './components/HomeStep';
import { Paywall } from './components/Paywall';
import { AuthStep } from './components/AuthStep';
import { ProfileStep } from './components/ProfileStep';
import { LandingPage } from './components/LandingPage';
import { BlogList } from './components/BlogList';
import { BlogPostView } from './components/BlogPost';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { ContactPage } from './components/ContactPage';
import { SupportPage } from './components/SupportPage';
import { TalkToTaskPrivacy } from './components/TalkToTaskPrivacy';
import { TalkToTaskTerms } from './components/TalkToTaskTerms';
import { TalkToTaskSupport } from './components/TalkToTaskSupport';
import { AppStep, ScenarioConfig, UserProfile, SavedChat, ChatMessage } from './types';
import { BLOG_POSTS } from './data/blogData';
import { auth } from './services/firebase';
import * as firebaseAuth from 'firebase/auth';
import { getUserProfile, getUserChats, saveUserProfile, saveChat, deleteChat, updateUserSubscription } from './services/dbService';

// Fix for firebase/auth types not exporting members correctly in some environments
const { onAuthStateChanged, signOut } = firebaseAuth as any;

// Global styles for animations
const GlobalStyles = () => (
  <style>{`
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in { animation: fadeIn 0.5s ease-out; }
    .animate-fade-in-up { animation: fadeInUp 0.5s ease-out; }
    .animate-slide-up { animation: slideUp 0.3s ease-out; }
    
    .pb-safe {
      padding-bottom: env(safe-area-inset-bottom);
    }
  `}</style>
);

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Error Boundary Component
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  declare props: Readonly<ErrorBoundaryProps>;

  public state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Something went wrong</h2>
          <p className="text-slate-500 mb-4 text-sm max-w-xs mx-auto">
            {this.state.error?.message || "An unexpected error occurred while loading the chat."}
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            Reload App
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Helper to determine initial step based on URL
const getInitialState = () => {
  const path = window.location.pathname;
  let showPricing = false;

  if (path === '/pricing' || path === '/pricing/') {
    showPricing = true;
    return { step: AppStep.Landing, postId: null, showPricing };
  }

  if (path === '/talktotaskprivacy' || path === '/talktotaskprivacy/') {
    return { step: AppStep.TalkToTaskPrivacy, postId: null, showPricing };
  }

  if (path === '/talktotaskterms' || path === '/talktotaskterms/') {
    return { step: AppStep.TalkToTaskTerms, postId: null, showPricing };
  }

  if (path === '/talktotasksupport' || path === '/talktotasksupport/') {
    return { step: AppStep.TalkToTaskSupport, postId: null, showPricing };
  }

  if (path.startsWith('/blog/')) {
    const postId = path.split('/blog/')[1];
    if (postId && BLOG_POSTS.some(p => p.id === postId)) {
      return { step: AppStep.BlogPost, postId: postId, showPricing };
    }
  }
  if (path === '/blog' || path === '/blog/') {
    return { step: AppStep.BlogList, postId: null, showPricing };
  }
  if (path === '/privacy' || path === '/privacy/') {
    return { step: AppStep.Privacy, postId: null, showPricing };
  }
  if (path === '/terms' || path === '/terms/') {
    return { step: AppStep.Terms, postId: null, showPricing };
  }
  if (path === '/contact' || path === '/contact/') {
    return { step: AppStep.Contact, postId: null, showPricing };
  }
  if (path === '/support' || path === '/support/') {
    return { step: AppStep.Support, postId: null, showPricing };
  }
  return { step: AppStep.Landing, postId: null, showPricing };
};

const App: React.FC = () => {
  const initial = getInitialState();
  const [step, setStep] = useState<AppStep>(initial.step);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authInitialView, setAuthInitialView] = useState<'login' | 'signup'>('login');
  
  // Guest Mode
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [pendingGuestTransfer, setPendingGuestTransfer] = useState(false);
  
  // Blog State
  const [selectedPostId, setSelectedPostId] = useState<string | null>(initial.postId);

  // Persistence
  const [savedChats, setSavedChats] = useState<SavedChat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  
  // Subscription State
  const [daysRemaining, setDaysRemaining] = useState<number>(0);
  const [showPaywall, setShowPaywall] = useState(initial.showPricing || false);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const state = getInitialState();
      setStep(state.step);
      setSelectedPostId(state.postId);
      setShowPaywall(state.showPricing || false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Helper to check access
  const checkSubscriptionAccess = (profile: UserProfile): boolean => {
    // Guests always have "access" to the UI until they hit the message limit in ChatInterface
    if (profile.isGuest) return true;

    // If no expiry date set (legacy users or error), default to expired or give 1 day grace.
    const expiry = profile.subscriptionExpiryDate || 0;
    const now = Date.now();
    const msPerDay = 1000 * 60 * 60 * 24;
    
    const diff = expiry - now;
    const daysLeft = Math.ceil(diff / msPerDay);
    
    setDaysRemaining(daysLeft > 0 ? daysLeft : 0);
    
    // Return true if active
    return diff > 0;
  };

  // Load state on mount and listen to Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: any) => {
      if (user) {
        // User is logged in
        setIsGuestMode(false);

        try {
          // CHECK FOR DATA MIGRATION FROM GUEST MODE
          if (pendingGuestTransfer && userProfile && userProfile.isGuest) {
             console.log("Transferring guest data to new user:", user.uid);
             
             // 1. Prepare Full Profile based on Guest data + default trial
             const msPerDay = 1000 * 60 * 60 * 24;
             const trialDuration = 7 * msPerDay;
             const newExpiry = Date.now() + trialDuration;
             
             const finalProfile: UserProfile = {
                 ...userProfile,
                 isGuest: false,
                 planType: 'trial',
                 subscriptionExpiryDate: newExpiry
             };

             // 2. Save Profile to DB
             await saveUserProfile(user.uid, finalProfile);
             
             // 3. Save Pending Chats to DB
             for (const chat of savedChats) {
                 await saveChat(user.uid, chat);
             }

             // 4. Update Local State
             setUserProfile(finalProfile);
             checkSubscriptionAccess(finalProfile);
             setPendingGuestTransfer(false);

             // 5. Navigate
             if (currentChatId) {
                 setStep(AppStep.Chat);
             } else {
                 setStep(AppStep.Home);
             }
          } else {
              // NORMAL LOGIN FLOW
              let profile = await getUserProfile(user.uid);
              
              if (profile) {
                 // AUTO-HEAL: If subscription data is missing (legacy user or error), grant 7 day trial
                 if (!profile.subscriptionExpiryDate) {
                    console.log("Fixing missing subscription date for user.");
                    const msPerDay = 1000 * 60 * 60 * 24;
                    const trialDuration = 7 * msPerDay; // 7 Days Trial
                    const newExpiry = Date.now() + trialDuration;
                    
                    // Update DB
                    await updateUserSubscription(user.uid, 'trial', newExpiry);
                    
                    // Update local profile object so logic below works
                    profile.subscriptionExpiryDate = newExpiry;
                    profile.planType = 'trial';
                }
    
                setUserProfile(profile);
                
                // Calculate Access
                const hasAccess = checkSubscriptionAccess(profile);
                
                if (hasAccess) {
                    const chats = await getUserChats(user.uid);
                    setSavedChats(chats);
                    // Only redirect to Home if we are in Auth or Landing or Onboarding
                    const restrictedSteps = [AppStep.Auth, AppStep.Onboarding, AppStep.Landing];
                    if (restrictedSteps.includes(step)) {
                        setStep(AppStep.Home);
                    }
                } else {
                    // User exists but subscription expired
                    setStep(AppStep.Home); 
                    setShowPaywall(true); 
                    const chats = await getUserChats(user.uid);
                    setSavedChats(chats);
                }
    
              } else {
                // New user (needs onboarding)
                setStep(AppStep.Onboarding);
              }
          }
        } catch (error) {
          console.error("Error loading user data:", error);
          setStep(AppStep.Onboarding);
        }
      } else {
        // Not logged in. 
        if (isGuestMode) {
            // Do nothing, let the guest play
        } else {
             // If we are already on Auth, stay there. Otherwise, show Landing.
            const allowedUnauthSteps = [
                AppStep.Auth, AppStep.BlogList, AppStep.BlogPost, 
                AppStep.Privacy, AppStep.Terms, AppStep.Contact, AppStep.Support,
                AppStep.TalkToTaskPrivacy, // Allow TalkToTaskPrivacy access without login
                AppStep.TalkToTaskTerms, // Allow TalkToTaskTerms access without login
                AppStep.TalkToTaskSupport, // Allow TalkToTaskSupport access without login
                AppStep.Onboarding, AppStep.Scenario, AppStep.Chat
            ];
            if (!allowedUnauthSteps.includes(step)) {
                setUserProfile(null);
                setSavedChats([]);
                setStep(AppStep.Landing);
            }
        }
      }
      setIsLoadingAuth(false);
    });

    return () => unsubscribe();
  }, [pendingGuestTransfer, isGuestMode]); // Added dependencies to ensure transfer logic picks up

  const handleAuthComplete = () => {
    // Handled by onAuthStateChanged
  };

  const handleLandingLogin = () => {
    setAuthInitialView('login');
    setStep(AppStep.Auth);
  };

  const handleLandingSignup = () => {
    setAuthInitialView('signup');
    setStep(AppStep.Auth);
  };

  // GUEST MODE ENTRY
  const handleLandingTryDemo = () => {
      setIsGuestMode(true);
      setUserProfile(null); // Clear any old state
      setSavedChats([]);
      setStep(AppStep.Onboarding);
  };

  const handleGuestSignupPrompt = () => {
      setPendingGuestTransfer(true);
      setAuthInitialView('signup');
      setStep(AppStep.Auth);
  };

  const handleLandingBlog = () => {
      window.history.pushState(null, '', '/blog');
      setStep(AppStep.BlogList);
  };

  const handleLandingPricing = () => {
      window.history.pushState(null, '', '/pricing');
      setShowPaywall(true);
  };

  const handleLandingPrivacy = () => {
      window.history.pushState(null, '', '/privacy');
      setStep(AppStep.Privacy);
  };

  const handleLandingTerms = () => {
      window.history.pushState(null, '', '/terms');
      setStep(AppStep.Terms);
  };

  const handleLandingContact = () => {
      window.history.pushState(null, '', '/contact');
      setStep(AppStep.Contact);
  };
  
  const handleReadBlogPost = (postId: string) => {
      window.history.pushState(null, '', `/blog/${postId}`);
      setSelectedPostId(postId);
      setStep(AppStep.BlogPost);
  };

  const handleBackToBlogList = () => {
      window.history.pushState(null, '', '/blog');
      setStep(AppStep.BlogList);
  };

  const handleBackToLanding = () => {
      if (isGuestMode) {
          if (window.confirm("Leaving now will lose your guest session progress. Are you sure?")) {
              setIsGuestMode(false);
              window.history.pushState(null, '', '/');
              setStep(AppStep.Landing);
          }
      } else {
        window.history.pushState(null, '', '/');
        setStep(AppStep.Landing);
      }
  };

  const handleOnboardingComplete = async (partialProfile: UserProfile, requestSubscribe: boolean = false) => {
    const msPerDay = 1000 * 60 * 60 * 24;
    const trialDuration = 7 * msPerDay; // 7 Days Trial
    const expiryDate = Date.now() + trialDuration;

    const fullProfile: UserProfile = {
        ...partialProfile,
        planType: 'trial',
        subscriptionExpiryDate: expiryDate,
        isGuest: isGuestMode
    };

    if (isGuestMode) {
        // Just store in local state, don't save to DB
        setUserProfile(fullProfile);
        setStep(AppStep.Scenario); // Guests go straight to scenario
        
        // If guest requested subscribe, prompt them to sign up first
        if (requestSubscribe) {
            handleGuestSignupPrompt();
        }
        return;
    }

    if (!auth.currentUser) return;

    try {
        await saveUserProfile(auth.currentUser.uid, fullProfile);
        setUserProfile(fullProfile);
        checkSubscriptionAccess(fullProfile);
        setStep(AppStep.Home);
        
        // If logged in user requested subscribe, show paywall immediately
        if (requestSubscribe) {
            setShowPaywall(true);
        }
    } catch (e) {
        console.error("Failed to save profile", e);
    }
  };

  const checkAccessAndShowPaywall = (): boolean => {
    if (!userProfile) return false;
    const hasAccess = checkSubscriptionAccess(userProfile);
    if (!hasAccess) {
        setShowPaywall(true);
        return false;
    }
    return true;
  };

  const handleNewChatClick = () => {
    if (checkAccessAndShowPaywall()) {
        setStep(AppStep.Scenario);
    }
  };

  const handleScenarioStart = async (config: ScenarioConfig) => {
    // For guests, we don't block access here, we block in ChatInterface
    if (!isGuestMode && !checkAccessAndShowPaywall()) return;

    const newChatId = Date.now().toString();
    const newChat: SavedChat = {
      id: newChatId,
      scenario: config,
      messages: [],
      lastUpdated: Date.now(),
      averageScore: 0
    };
    
    setSavedChats(prevChats => [...prevChats, newChat]);
    setCurrentChatId(newChatId);
    setStep(AppStep.Chat);

    if (!isGuestMode && auth.currentUser) {
      try {
        await saveChat(auth.currentUser.uid, newChat);
      } catch (e) {
        console.error("Failed to save initial chat to Firestore", e);
      }
    }
  };

  const handleOpenChat = (chatId: string) => {
    if (checkAccessAndShowPaywall()) {
        setCurrentChatId(chatId);
        setStep(AppStep.Chat);
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    if (!window.confirm("Are you sure you want to delete this conversation? This cannot be undone.")) {
      return;
    }
    
    setSavedChats(prev => prev.filter(c => c.id !== chatId));
    
    if (!isGuestMode && auth.currentUser) {
      try {
        await deleteChat(auth.currentUser.uid, chatId);
      } catch (e) {
        console.error("Failed to delete chat", e);
      }
    }
  };

  const handleChatUpdate = async (messages: ChatMessage[], averageScore: number) => {
    if (!currentChatId) return;

    const currentChatIndex = savedChats.findIndex(c => c.id === currentChatId);
    if (currentChatIndex === -1) return;

    const updatedChatObj: SavedChat = {
        ...savedChats[currentChatIndex],
        messages: messages,
        lastUpdated: Date.now(),
        averageScore: averageScore
    };

    setSavedChats(prevChats => {
        const newChats = [...prevChats];
        newChats[currentChatIndex] = updatedChatObj;
        return newChats;
    });

    if (!isGuestMode && auth.currentUser) {
        try {
            await saveChat(auth.currentUser.uid, updatedChatObj);
        } catch (e) {
            console.error("Failed to save updated chat to Firestore", e);
        }
    }
  };

  const handleExitChat = () => {
    if (isGuestMode) {
        // Guests only have one chat, if they exit, they can't save multiple drafts easily in this simplified version
        // so we can just warn them or let them go back to landing/scenario
        if(window.confirm("Exit chat? Your guest session progress might be lost if you refresh.")) {
             setCurrentChatId(null);
             setStep(AppStep.Scenario); // Guests go back to create new scenario usually
        }
    } else {
        setCurrentChatId(null);
        setStep(AppStep.Home);
    }
  };

  const handleUpgradeClick = () => {
      if (isGuestMode) {
          handleGuestSignupPrompt();
      } else {
          setShowPaywall(true);
      }
  };

  const handleProfileClick = () => {
    if (isGuestMode) {
        handleGuestSignupPrompt();
    } else {
        setStep(AppStep.Profile);
    }
  };
  
  const handleProfileUpdate = async (updatedProfile: UserProfile) => {
    if (!auth.currentUser) return;
    try {
        await saveUserProfile(auth.currentUser.uid, updatedProfile);
        setUserProfile(updatedProfile);
    } catch (e) {
        console.error("Failed to update profile", e);
        alert("Failed to save profile changes.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.history.pushState(null, '', '/');
    setStep(AppStep.Landing);
    setUserProfile(null);
    setSavedChats([]);
    setShowPaywall(false);
  };

  const handleSubscribe = async (plan: 'monthly' | 'yearly') => {
    if (!auth.currentUser) return;

    // We no longer update the DB here. The Cloud Function 'verifyPayment' 
    // does the DB update securely.
    // We just update the local state to reflect the change immediately.
    
    const msPerDay = 1000 * 60 * 60 * 24;
    const duration = plan === 'monthly' ? 30 : 365;
    const addedTime = duration * msPerDay;
    
    const currentExpiry = userProfile?.subscriptionExpiryDate || 0;
    const now = Date.now();
    const baseTime = currentExpiry > now ? currentExpiry : now;
    const newExpiry = baseTime + addedTime;

    // Update local state ONLY (Server is already updated by Paywall component)
    if (userProfile) {
        const updatedProfile = { 
            ...userProfile, 
            planType: plan, 
            subscriptionExpiryDate: newExpiry 
        };
        setUserProfile(updatedProfile);
        checkSubscriptionAccess(updatedProfile);
    }

    setShowPaywall(false);
    alert(`Success! You have added ${duration} days to your subscription.`);
  };

  const currentChat = savedChats.find(c => c.id === currentChatId);

  if (isLoadingAuth) {
      return (
          <div className="fixed inset-0 bg-slate-50 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin"></div>
          </div>
      );
  }

  // Define steps where the header should NOT be shown
  const noHeaderSteps = [
      AppStep.Landing, 
      AppStep.Auth, 
      AppStep.Profile, 
      AppStep.BlogList, 
      AppStep.BlogPost, 
      AppStep.Privacy, 
      AppStep.Terms, 
      AppStep.Contact,
      AppStep.Support,
      AppStep.TalkToTaskPrivacy, // Added TalkToTaskPrivacy
      AppStep.TalkToTaskTerms, // Added TalkToTaskTerms
      AppStep.TalkToTaskSupport // Added TalkToTaskSupport
  ];

  // Logic for Paywall Dismissal
  // Dismissible if: User is NOT logged in (viewing pricing page) OR User IS logged in but has active subscription (upgrading)
  const isPaywallDismissible = !userProfile || daysRemaining > 0;

  return (
    <div className="fixed inset-0 flex flex-col bg-slate-50 font-sans text-slate-900 overflow-hidden">
      <GlobalStyles />
      
      {/* Header logic */}
      {!noHeaderSteps.includes(step) && (
        <div className="flex-none z-20">
            <Header 
                user={userProfile} 
                daysRemaining={daysRemaining} 
                onUpgrade={handleUpgradeClick}
                showBack={step === AppStep.Chat || step === AppStep.Scenario}
                onBack={handleExitChat}
                onProfileClick={handleProfileClick}
            />
            {isGuestMode && (
                <div className="bg-brand-50 px-4 py-2 text-center text-xs text-brand-700 font-semibold border-b border-brand-100">
                    Guest Mode: Messages are limited. <button onClick={handleGuestSignupPrompt} className="underline">Sign up</button> to save your progress.
                </div>
            )}
        </div>
      )}

      <main className="flex-1 w-full mx-auto overflow-hidden relative z-10">
        
        {step === AppStep.Landing && (
            <div className="h-full overflow-y-auto">
                <LandingPage 
                    onLogin={handleLandingLogin} 
                    onSignup={handleLandingSignup} 
                    onTryDemo={handleLandingTryDemo}
                    onBlogClick={handleLandingBlog}
                    onPricingClick={handleLandingPricing}
                    onPrivacyClick={handleLandingPrivacy}
                    onTermsClick={handleLandingTerms}
                    onContactClick={handleLandingContact}
                />
            </div>
        )}

        {step === AppStep.BlogList && (
            <div className="h-full overflow-y-auto">
                <BlogList 
                    posts={BLOG_POSTS} 
                    onReadPost={handleReadBlogPost} 
                    onBack={handleBackToLanding} 
                />
            </div>
        )}

        {step === AppStep.BlogPost && selectedPostId && (
            <div className="h-full overflow-y-auto">
                <BlogPostView 
                    post={BLOG_POSTS.find(p => p.id === selectedPostId) || BLOG_POSTS[0]} 
                    onBack={handleBackToBlogList} 
                />
            </div>
        )}

        {step === AppStep.Privacy && (
            <div className="h-full overflow-y-auto">
                <PrivacyPolicy onBack={handleBackToLanding} />
            </div>
        )}

        {step === AppStep.Terms && (
            <div className="h-full overflow-y-auto">
                <TermsOfService onBack={handleBackToLanding} />
            </div>
        )}

        {step === AppStep.Contact && (
            <div className="h-full overflow-y-auto">
                <ContactPage onBack={handleBackToLanding} />
            </div>
        )}

        {step === AppStep.Support && (
            <div className="h-full overflow-y-auto">
                <SupportPage />
            </div>
        )}

        {/* TalkToTask Privacy Page */}
        {step === AppStep.TalkToTaskPrivacy && (
            <div className="h-full overflow-y-auto">
                <TalkToTaskPrivacy onBack={handleBackToLanding} />
            </div>
        )}

        {/* TalkToTask Terms Page */}
        {step === AppStep.TalkToTaskTerms && (
            <div className="h-full overflow-y-auto">
                <TalkToTaskTerms onBack={handleBackToLanding} />
            </div>
        )}

        {/* TalkToTask Support Page */}
        {step === AppStep.TalkToTaskSupport && (
            <div className="h-full overflow-y-auto">
                <TalkToTaskSupport onBack={handleBackToLanding} />
            </div>
        )}

        {step === AppStep.Auth && (
            <div className="h-full overflow-y-auto bg-slate-50">
                <AuthStep 
                  onAuthComplete={handleAuthComplete} 
                  initialView={authInitialView} 
                  onBack={handleBackToLanding}
                />
            </div>
        )}

        {/* Max width container for inner app steps */}
        <div className={`h-full ${noHeaderSteps.includes(step) ? '' : 'max-w-2xl mx-auto'}`}>
            
            {step === AppStep.Onboarding && (
            <div className="h-full overflow-y-auto">
                <OnboardingStep onComplete={handleOnboardingComplete} />
            </div>
            )}

            {step === AppStep.Home && savedChats && (
            <HomeStep 
                chats={savedChats} 
                onNewChat={handleNewChatClick} 
                onOpenChat={handleOpenChat}
                onDeleteChat={handleDeleteChat}
            />
            )}

            {step === AppStep.Scenario && userProfile && (
            <ScenarioStep user={userProfile} onStart={handleScenarioStart} />
            )}

            {step === AppStep.Chat && (
            currentChat && userProfile ? (
                <ErrorBoundary>
                    <ChatInterface 
                    key={currentChat.id}
                    user={userProfile} 
                    scenario={currentChat.scenario} 
                    initialMessages={currentChat.messages}
                    onUpdateChat={handleChatUpdate}
                    onGuestSignupPrompt={handleGuestSignupPrompt}
                    />
                </ErrorBoundary>
            ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                    <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mb-4"></div>
                    <p>Loading conversation...</p>
                </div>
            )
            )}
            
            {step === AppStep.Profile && userProfile && (
                <ProfileStep 
                    user={userProfile} 
                    onUpdate={handleProfileUpdate}
                    onLogout={handleLogout}
                    onUpgrade={handleUpgradeClick}
                    onBack={() => setStep(AppStep.Home)}
                />
            )}
        </div>
      </main>

      {showPaywall && (
        <Paywall 
          email={auth.currentUser?.email || 'user@example.com'}
          onSubscribe={handleSubscribe} 
          onCancel={() => {
              if (isPaywallDismissible) {
                  setShowPaywall(false);
                  // Return to home if on pricing route
                  if (window.location.pathname === '/pricing') {
                      window.history.pushState(null, '', '/');
                  }
              }
          }} 
          isDismissible={isPaywallDismissible}
          onSignOut={handleLogout}
        />
      )}
    </div>
  );
};

export default App;